/*
 * Design note for this page:
 * Cybernetic Editorial Observatory applied to privacy governance.
 * The page should feel globally credible, PDPA-first for Singapore, and clear about
 * transparency, user rights, controlled retention, and data-protection boundaries.
 */

import PolicyLayout from "@/components/PolicyLayout";
import { Globe2, LockKeyhole, ShieldCheck } from "lucide-react";

const cards = [
  {
    title: "PDPA-first handling",
    body: "Because Command Secrets is offered for Singapore citizens and Singapore permanent residents, the privacy posture is written with strong emphasis on Singapore PDPA expectations for collection, use, disclosure, and care of personal data.",
    icon: ShieldCheck,
  },
  {
    title: "Global rights awareness",
    body: "The page also addresses transparency and rights expectations associated with the GDPR, UK GDPR, CCPA/CPRA, and other applicable privacy laws where they may apply to users, visitors, or business operations.",
    icon: Globe2,
  },
  {
    title: "Controlled access",
    body: "Access to customer data, reports, and owner-level settings is expected to be limited by role, password controls, operational need, and security monitoring rather than open internal visibility.",
    icon: LockKeyhole,
  },
];

const sections = [
  {
    id: "scope",
    title: "Scope, identity, and privacy approach",
    intro:
      "This privacy notice describes how Command Secrets handles personal data on the website, in customer support, and in any related report, subscription, or app-access flow.",
    paragraphs: [
      "For Singapore-facing operations, Command Secrets treats the Personal Data Protection Act as a core baseline for the collection, use, disclosure, and protection of personal data. The service also aims to present a transparency standard that aligns in practical terms with major global privacy regimes, including the GDPR, UK GDPR, and California consumer privacy rules where applicable.",
      "The purpose of this page is to explain what categories of information may be collected, why they may be used, how long they may be retained, what rights users may request, and how Command Secrets approaches security, support, and lawful operational disclosure. This page is written in plain customer-facing language and does not replace any narrower contract, platform term, or legally required request-verification process that may apply in a specific case.",
      "Where the website or app introduces new features, plan types, data fields, or integrations, this notice may be updated so the public-facing explanation remains accurate and reasonably current.",
    ],
  },
  {
    id: "data-collected",
    title: "Categories of personal data that may be collected",
    intro:
      "Command Secrets seeks to collect only data that is reasonably necessary for account access, support, billing administration, individualized analysis, security operations, and service improvement.",
    paragraphs: [
      "Depending on the product flow, this may include identity and contact details, login or account references, billing-related records, subscription history, customer-service correspondence, device and browser signals, report-delivery logs, and user-provided analytical inputs such as birth-related details, names, language preferences, and behavioural decision logs.",
      "If a customer uploads, submits, or types information into the service, that information may be stored and processed to generate individualized results, customer support replies, delivery records, anti-fraud checks, and account continuity. Command Secrets does not ask customers to disclose more information than is reasonably necessary for the requested service path, and users should avoid submitting third-party personal data unless they are authorised to do so.",
      "Some technical data may also be recorded automatically for service integrity, including IP-related logs, request timestamps, browser details, security events, crash diagnostics, broken-link detection, and anti-abuse indicators used to protect the website and customer outputs.",
    ],
  },
  {
    id: "use-and-disclosure",
    title: "How personal data may be used and disclosed",
    intro:
      "Command Secrets uses personal data for limited service purposes that are tied to customer expectations, lawful business operations, and platform protection.",
    paragraphs: [
      "Typical uses may include providing access to the website or application, creating and delivering analytical outputs, managing subscriptions, verifying ownership of an account, responding to billing or support requests, improving service reliability, detecting abuse, handling legal or regulatory obligations, and preserving evidence where account misuse, fraud, or intrusion is suspected.",
      "Command Secrets does not present this public page as a promise that personal data will never be disclosed under any circumstances. Data may be shared with hosting providers, infrastructure vendors, payment processors, technical contractors, legal advisers, or competent authorities where such sharing is reasonably necessary for operating the service, protecting rights, investigating abuse, or complying with law.",
      "Command Secrets does not sell personal data in the ordinary commercial sense described by many privacy laws. If a jurisdiction grants a right to request information about sharing, correction, deletion, access, or restriction, Command Secrets may require reasonable verification before acting on that request so that customer data is not disclosed to the wrong person.",
    ],
  },
  {
    id: "global-rights",
    title: "Global privacy rights and regional requests",
    intro:
      "Users may have different statutory rights depending on where they live and how the service is offered to them.",
    paragraphs: [
      "For Singapore users, Command Secrets aims to respect core PDPA expectations around consent management where required, appropriate notification, access and correction handling, protection of personal data, and controlled retention. For users in the European Union or United Kingdom, the service also recognises that transparency, access, rectification, erasure, restriction, objection, portability, and complaint rights may apply in specific circumstances under the GDPR or UK GDPR.",
      "For California residents, the service also recognises that rights may include knowing what personal information is collected and how it is used, requesting deletion subject to legal exceptions, correcting inaccurate information, limiting certain sensitive-information uses, opting out of sale or sharing where relevant, and being free from discrimination for exercising statutory privacy rights.",
      "Because rights depend on law, geography, customer status, and technical context, Command Secrets may ask for enough information to verify identity, narrow the request, or explain why part of a request cannot be fulfilled exactly as submitted. Requests may be declined where law permits refusal, where identity cannot be verified, where retention is required, or where disclosure would adversely affect the rights or security of others.",
    ],
  },
  {
    id: "retention-security",
    title: "Retention, security, and cross-border handling",
    intro:
      "Personal data should not remain accessible forever by default, and it should not be exposed casually inside the service operation.",
    paragraphs: [
      "Command Secrets may retain data for as long as reasonably necessary to provide the requested service, maintain account continuity, answer disputes, prevent fraud, preserve report history for legitimate customer retrieval, comply with legal obligations, or protect the business where misuse, chargebacks, or security events need review. Different categories of data may therefore have different retention periods.",
      "Security controls may include password protection, restricted internal access, logging, infrastructure-level safeguards, watermarking for report outputs, security review of broken-link and crash events, and technical controls designed to reduce crawling, cloning, scraping, unauthorised developer-tool interference, and hostile intervention against customer-facing surfaces. No online system can promise absolute security, so users should also protect their own credentials and devices.",
      "Because digital infrastructure may involve cloud providers, regional vendors, and globally distributed technical services, personal data may be stored or processed outside a user's home jurisdiction. Where cross-border handling occurs, Command Secrets aims to do so under reasonable contractual, technical, or operational safeguards appropriate to the service context.",
    ],
  },
  {
    id: "contact",
    title: "Privacy requests, marketing preferences, and contact path",
    intro:
      "Customers should have a clear path for privacy, support, and data-governance questions.",
    paragraphs: [
      "Privacy, access, correction, deletion, objection, withdrawal, support, and billing-related requests should be submitted through the public support path so they can be reviewed and logged through a controlled operational process. Command Secrets may need to confirm identity before releasing, correcting, or deleting account-linked information.",
      "Where marketing communications are introduced, Command Secrets aims to provide unsubscribe or opt-out controls appropriate to the communication channel. Singapore telephone marketing practices, if ever used, would also need to respect Do Not Call requirements where applicable.",
      "If Command Secrets materially changes the way personal data is handled, this notice may be revised so that users can review the updated public position before continuing to use the service.",
    ],
  },
];

const relatedLinks = [
  {
    label: "Disclaimer",
    href: "/legal/disclaimer",
    description: "Review the boundaries around public-figure references, illustrative data, and proprietary automated analysis.",
  },
  {
    label: "Cancellation terms",
    href: "/legal/cancellation",
    description: "Understand cancellations, no-refund rules after rendered analysis, and manual plan-management requests.",
  },
  {
    label: "Support",
    href: "/support",
    description: "Use the public support path for privacy requests, billing questions, upgrade or downgrade handling, and service issues.",
  },
  {
    label: "Security",
    href: "/security",
    description: "See the public-facing security posture, anti-abuse expectations, and resilience commitments for the website and reports.",
  },
];

const jurisdictionSwitches = [
  {
    label: "Global notice",
    href: "#global-rights",
    description: "Jump to the section that explains how regional privacy rights may differ by geography and legal context.",
  },
  {
    label: "Singapore legal page",
    href: "/legal/singapore",
    description: "Open the Singapore-specific PDPA, communication, and support wording for local users and residents.",
  },
  {
    label: "UK legal page",
    href: "/legal/uk",
    description: "Open the UK-specific transparency, rights, and support-handling wording for visitors in the United Kingdom.",
  },
];

export default function Privacy() {
  const featurePanel = (
    <div className="rounded-[1.7rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.22)] md:p-7">
      <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--signal-soft)]">Jurisdiction switch</p>
      <h2 className="mt-4 font-display text-3xl text-white md:text-4xl">Move quickly between the global notice and region-specific legal wording.</h2>
      <p className="mt-4 max-w-3xl text-sm leading-7 text-white/66 md:text-base">
        Use these links when you need the broader privacy explanation first, then the Singapore or United Kingdom wording that applies to more specific regional questions.
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {jurisdictionSwitches.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className="rounded-[1.2rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-4 transition hover:border-[rgba(191,59,80,0.32)] hover:bg-[rgba(131,12,28,0.08)]"
          >
            <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--signal-soft)]">Open route</div>
            <h3 className="mt-3 font-display text-2xl text-white">{item.label}</h3>
            <p className="mt-3 text-sm leading-7 text-white/64">{item.description}</p>
          </a>
        ))}
      </div>
    </div>
  );

  return (
    <PolicyLayout
      eyebrow="Privacy notice"
      title="A Singapore-first privacy posture with global transparency and rights awareness."
      intro="Command Secrets is aimed at Singapore citizens and Singapore permanent residents, but it presents its privacy position in a way that also recognises global expectations for transparency, lawful use, data protection, customer rights, and controlled operational disclosure. This page explains those commitments in public-facing language."
      highlight="Collect what is needed, explain why it is used, restrict access, and protect the service surface."
      cards={cards}
      featurePanel={featurePanel}
      sections={sections}
      relatedLinks={relatedLinks}
    />
  );
}
